import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { motion } from "framer-motion";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { ProfileAvatar, ProfileAvatarHeroWithFallback } from "./ProfileAvatar";
import { UserAvatar } from "./UserAvatar";
import { ProfileLink } from "./ProfileLink";
import Cookies from "js-cookie";
import { useUser } from "../../Data/UserContext";
import { getAuthHeaders, getVerifySessionUrl } from "../../utils/authHeaders";
import type { User, UserGame } from "../../Data/UserContext";
import {
  AVATAR_OPTIONS_CACHE_KEY,
  FALLBACK_AVATAR_OPTIONS,
  getAnimalPreviewAvatars,
  getAvatarThumbSrc,
  type AvatarCatalogItem,
} from "../../utils/profileAvatarCatalog";
import {
  normalizeSessionPayload,
  resolveAvatarUrl,
  showOnlineOnOwnProfile,
  toProfileHeroAvatarUrl,
} from "../../utils/sessionUser";
import {
  profileModalBackgroundState,
  type ProfileModalLocationState,
} from "../../utils/profileModalNavigation";
import { markMyZoneOnboardingMilestone } from "../../utils/myZoneOnboardingMilestones";
import {
  trackCtaClicked,
  trackInviteFlowOpened,
  trackProfileLinkShared,
  trackProfileUpdated,
} from "../../utils/frontendTrackingEvents";
import {
  buildProfileInviteShareText,
  buildProfileInviteShareTitle,
} from "../../utils/shareCopy";
import {
  useDebouncedWindowEvents,
  useVisibleStaleFallback,
} from "../../hooks/useSocialSyncRefresh";
import { useTraderDataFetcher } from "../../Data/TraderDataFetcherContext";
import FriendActivityTimeline, {
  mapDigestRows,
  type FriendDigest,
  type FriendListEntry,
  type MySquareGame,
  type MyTradeGame,
} from "../MyZone/FriendActivityTimeline";
import { ProfileCountryCombobox } from "./ProfileCountryCombobox";
import {
  ProfileFavouritesStrip,
  type ProfileFavChip,
} from "./ProfileFavouritesStrip";
import { CareerStatsStack } from "./CareerStatsStack";
import { CRICKET8_LOGO_SRC } from "../Logo/Logo";
import { ProfileUnlockBanner } from "./ProfileUnlockBanner";
import { AvatarChooserModal } from "./AvatarChooserModal";
import { XpHubModal } from "../Xp/XpHubModal";
import { ProfileFavouriteSportsGrid } from "./ProfileFavouriteSportsGrid";
import { ProfileFavouriteFootballGrid } from "./ProfileFavouriteFootballGrid";
import { ProfileFavouriteCricketTeamsGrid } from "./ProfileFavouriteCricketTeamsGrid";
import { ProfileFavouriteCricketersGrid } from "./ProfileFavouriteCricketersGrid";
import {
  parseFavSportsFromStorage,
  serializeFavSportsToStorage,
  type ProfileFavSportOption,
} from "./profileFavSportIcons";
import {
  parseFavFootballFromStorage,
  serializeFavFootballToStorage,
  favFootballLabel,
  favFootballLongLabel,
  type ProfileFavFootballSlug,
} from "./profileFavFootballTeams";
import {
  parseFavIplFromStorage,
  serializeFavIplToStorage,
  favIplLabel,
  favIplLongLabel,
  type ProfileFavIplSlug,
} from "./profileFavIplTeams";
import {
  parseFavCricketIntFromStorage,
  serializeFavCricketIntToStorage,
  favCricketIntLabel,
  type ProfileFavCricketIntSlug,
} from "./profileFavCricketIntTeams";
import {
  parseFavTeamsFromStorage,
  slugifyTeamName,
} from "../../utils/favouriteTeams";
import { canonicalTeamSlug } from "../../utils/cricketTeamCanonical";
import { useFavouriteTeams } from "../../Data/useFavouriteTeams";
import {
  optionalDisplayName,
  playerHandleName,
} from "../../utils/friendDisplayName";
import { ProfileNameStack } from "./ProfileNameStack";
import {
  parseFavCricketersFromStorage,
  serializeFavCricketersToStorage,
  favCricketerLabel,
  type ProfileFavCricketerSlug,
} from "./profileFavCricketers";
import { ModalCloseButton } from "../ModalCloseIcon";
import {
  type CareerSummaryPayload,
  parseCareerSummaryPayload,
  fallbackCareerSummaryZeros,
} from "./careerSummary";
import {
  countryToSelectValue,
  getProfileCountryFlagEmoji,
} from "../../utils/profileCountryFlag";
import { nativeShareData } from "../../utils/nativeShare";

function openShopDrawer() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("openShopDrawer"));
  }
}

/** Background for edit-profile inputs and selects. */
const profileEditFieldBgClass = "bg-[#4f2189]";

/** Label + control + unlock rail in one row; inputs sit directly beside labels (flex-1 fills the rest). */
const profileEditFormRowFlexClass =
  "flex h-[50px] min-h-[50px] max-h-[50px] w-full min-w-0 flex-nowrap items-center gap-1.5";

const profileEditFormMiddleCellClass =
  "flex min-w-0 flex-1 items-center overflow-hidden";

const profileEditFormLabelCellClass = "shrink-0 self-center";

const profileEditFormLabelNowrapClass = "whitespace-nowrap";

/** Align Player name / Name / Avatar / Country fields: equal label column width. */
const profileEditFormTopFieldLabelMinClass = "min-w-[5.5rem]";

const profileEditFormRailCellClass =
  "flex h-[50px] w-[43px] min-w-[43px] max-w-[43px] shrink-0 items-center justify-end";

/** Player name / Name / Avatar / Country row labels (edit profile). */
const profileEditRowLabelClass =
  "font-anton text-[15px] font-normal not-italic uppercase leading-[12px] tracking-normal text-white [leading-trim:none]";

/** Player name / Name fields (edit profile) — Chakra Petch 12px / 16px line-height; 16px on mobile avoids iOS focus zoom. */
const profileEditUsernameInputClass =
  "font-chakra text-[16px] font-normal not-italic leading-[16px] tracking-normal [leading-trim:none] md:text-[12px]";

/** Cancel control (edit profile header) — Chakra Petch 11px / 14px mobile; 16px / 14px desktop. */
const profileEditCancelButtonClass =
  "font-chakra text-[11px] font-normal not-italic leading-[14px] tracking-normal text-right [leading-trim:none] md:text-[16px]";

/** Compact Unlock CTA beside username (edit profile) — Anton 12px / 12px line-height. */
const profileEditUsernameUnlockButtonClass =
  "inline-flex h-[21px] w-[43px] shrink-0 items-center justify-center overflow-hidden whitespace-nowrap bg-[#d2ff00] p-0 font-anton text-[12px] font-normal uppercase leading-[12px] tracking-normal text-[#1b062f] [leading-trim:none] hover:brightness-95";

/** API `error` values for /account/update that belong under the player name field. */
function isPlayerNameFieldApiError(data: { error?: unknown }): boolean {
  const code = data?.error;
  if (typeof code !== "string") return false;
  return (
    code === "name_taken" ||
    code === "player_name_taken" ||
    code === "username exists" ||
    code === "invalid_name" ||
    code === "name_not_allowed" ||
    code === "player_name_invalid" ||
    code === "player_name_blocked" ||
    code === "player_name_country" ||
    code === "player_name_edit_locked" ||
    code === "feature_disabled"
  );
}

/** API `error` values that belong under the Name (display_name) field. */
function isDisplayNameFieldApiError(data: { error?: unknown }): boolean {
  const code = data?.error;
  if (typeof code !== "string") return false;
  return (
    code === "display_name_invalid" ||
    code === "display_name_blocked" ||
    code === "display_name_edit_locked"
  );
}

function isUsernameAlreadyTakenApiError(data: { error?: unknown }): boolean {
  const code = data?.error;
  return (
    code === "name_taken" ||
    code === "player_name_taken" ||
    code === "username exists"
  );
}

const PLAYER_NAME_ALREADY_TAKEN_MESSAGE = "That player name is already in use.";

type FriendRow = {
  userId: number;
  player_id?: string | null;
  player_name?: string | null;
  display_name?: string | null;
  username?: string;
  profile_pic?: string | null;
  xp_week_crown?: string | null;
  avatar_frame_url?: string | null;
  avatar_frame_small_url?: string | null;
};

export default function ProfilePage(props?: {
  onClose?: () => void;
  embedInGlobalModal?: boolean;
  initialOpenEdit?: boolean;
  /** Full-page /edit-profile — always show editor; Cancel / after save leaves via onLeaveEditPage */
  editPageMode?: boolean;
  onLeaveEditPage?: (opts?: { openOwnProfileModal?: boolean }) => void;
}) {
  const {
    onClose,
    embedInGlobalModal = false,
    initialOpenEdit = false,
    editPageMode = false,
    onLeaveEditPage,
  } = props ?? {};
  const { user, setUser } = useUser();
  const { fetchMyGamesOverview } = useTraderDataFetcher();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [careerSummary, setCareerSummary] =
    useState<CareerSummaryPayload | null>(null);
  const [friendsStrip, setFriendsStrip] = useState<FriendRow[]>([]);
  const [ownActivityDigests, setOwnActivityDigests] = useState<FriendDigest[]>(
    []
  );
  const [ownActivityLiveFriends, setOwnActivityLiveFriends] = useState<
    FriendListEntry[]
  >([]);
  const [ownActivityMyTrades, setOwnActivityMyTrades] = useState<MyTradeGame[]>(
    []
  );
  const [ownActivityMySquares, setOwnActivityMySquares] = useState<
    MySquareGame[]
  >([]);
  const [ownActivityLoading, setOwnActivityLoading] = useState(false);
  const [ownActivityLoadingMore, setOwnActivityLoadingMore] = useState(false);
  const [ownActivityHasMore, setOwnActivityHasMore] = useState(false);
  const [ownActivityResultsLimit, setOwnActivityResultsLimit] = useState(10);
  const [ownActivityRefreshTick, setOwnActivityRefreshTick] = useState(0);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [isEditing, setIsEditing] = useState(() => editPageMode);
  const [editCountry, setEditCountry] = useState("");
  const [editFavSports, setEditFavSports] = useState<string[]>([]);
  const [editFavFootballSlugs, setEditFavFootballSlugs] = useState<string[]>(
    []
  );
  const [editFavIplSlugs, setEditFavIplSlugs] = useState<string[]>([]);
  const [editFavCricketIntSlugs, setEditFavCricketIntSlugs] = useState<
    string[]
  >([]);
  const [editFavCricketerSlugs, setEditFavCricketerSlugs] = useState<string[]>(
    []
  );
  const [editPlayerName, setEditPlayerName] = useState("");
  const [editPlayerNameError, setEditPlayerNameError] = useState<string | null>(
    null
  );
  const [editDisplayName, setEditDisplayName] = useState("");
  const [editDisplayNameError, setEditDisplayNameError] = useState<
    string | null
  >(null);
  const [saveFeedback, setSaveFeedback] = useState<{
    message: string;
    error?: boolean;
  } | null>(null);
  const [saving, setSaving] = useState(false);
  const [stripAvatarOptions, setStripAvatarOptions] = useState<
    AvatarCatalogItem[]
  >(FALLBACK_AVATAR_OPTIONS);
  const [avatarChooserOpen, setAvatarChooserOpen] = useState(false);
  const [xpHubOpen, setXpHubOpen] = useState(false);
  const [avatarChooserInitialKey, setAvatarChooserInitialKey] = useState<
    string | undefined
  >(undefined);
  const justUnlockedProfileTimeoutRef = useRef<ReturnType<
    typeof setTimeout
  > | null>(null);
  const editFormScrollRef = useRef<HTMLDivElement | null>(null);
  const [justUnlockedProfile, setJustUnlockedProfile] = useState(false);

  const {
    clubExtras,
    intExtras,
    toggle: toggleFavouriteTeam,
  } = useFavouriteTeams();

  const sessionToken = Cookies.get("c8_session_token");

  const animalPreviewAvatars = useMemo(() => {
    if (user?.allow_player_name_edit) return [];
    return getAnimalPreviewAvatars(stripAvatarOptions);
  }, [stripAvatarOptions, user?.allow_player_name_edit]);

  /** Own profile header only — not shown on public profiles. */
  const ownProfileHeroCountryFlag = useMemo(
    () => getProfileCountryFlagEmoji(user?.country),
    [user?.country]
  );

  useEffect(() => {
    if (!isEditing) return;
    let cancelled = false;
    (async () => {
      try {
        const cached = sessionStorage.getItem(AVATAR_OPTIONS_CACHE_KEY);
        if (cached) {
          const parsed = JSON.parse(cached) as unknown;
          if (Array.isArray(parsed) && parsed.length > 0 && !cancelled) {
            setStripAvatarOptions(parsed as AvatarCatalogItem[]);
          }
        }
      } catch {
        // ignore
      }
      try {
        const res = await fetch(`${process.env.API_URL}/account/avatars`, {
          method: "GET",
          headers: getAuthHeaders(),
        });
        if (!res.ok || cancelled) return;
        const data = (await res.json().catch(() => ({}))) as {
          avatars?: AvatarCatalogItem[];
        };
        if (Array.isArray(data?.avatars) && data.avatars.length > 0) {
          setStripAvatarOptions(data.avatars);
          try {
            sessionStorage.setItem(
              AVATAR_OPTIONS_CACHE_KEY,
              JSON.stringify(data.avatars)
            );
          } catch {
            // ignore
          }
        }
      } catch {
        // keep fallback
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEditing]);

  useEffect(() => {
    if (editPageMode) {
      setIsEditing(true);
      return;
    }
    if (embedInGlobalModal) {
      if (initialOpenEdit) setIsEditing(true);
      return;
    }
    if (searchParams.get("edit") === "1") setIsEditing(true);
  }, [searchParams, embedInGlobalModal, initialOpenEdit, editPageMode]);

  const didInitEditFormRef = useRef(false);
  useEffect(() => {
    if (isEditing && user) {
      if (!didInitEditFormRef.current) {
        didInitEditFormRef.current = true;
        setEditCountry(countryToSelectValue(user.country));
        setEditFavSports(parseFavSportsFromStorage(user.favourites?.favsports));
        setEditFavFootballSlugs(
          parseFavFootballFromStorage(user.favourites?.favfootballteams)
        );
        setEditFavIplSlugs(
          parseFavIplFromStorage(user.favourites?.faviplteams)
        );
        setEditFavCricketIntSlugs(
          parseFavCricketIntFromStorage(user.favourites?.favnationalteams)
        );
        setEditFavCricketerSlugs(
          parseFavCricketersFromStorage(user.favourites?.favcricketers)
        );
        setEditPlayerName(
          user.player_name?.trim() || user.username?.trim() || ""
        );
        // display_name stays empty by default; friends UI falls back to player_name until set.
        setEditDisplayName(user.display_name?.trim() || "");
        setEditDisplayNameError(null);
      }
    } else {
      didInitEditFormRef.current = false;
    }
  }, [isEditing, user]);

  // After Profile Unlock Pack purchase: celebrate on profile header (name + avatar), not Account
  useEffect(() => {
    if (user?.allow_player_name_edit === true) {
      if (sessionStorage.getItem("c8_just_unlocked_player_name") === "1") {
        sessionStorage.removeItem("c8_just_unlocked_player_name");
        setJustUnlockedProfile(true);
        if (justUnlockedProfileTimeoutRef.current)
          clearTimeout(justUnlockedProfileTimeoutRef.current);
        justUnlockedProfileTimeoutRef.current = setTimeout(
          () => setJustUnlockedProfile(false),
          1200
        );
      }
    }
  }, [user?.allow_player_name_edit]);

  useEffect(() => {
    const handleRunProfileUnlockAnimation = () => {
      if (sessionStorage.getItem("c8_just_unlocked_player_name") !== "1")
        return;
      if (!user?.allow_player_name_edit) return;
      sessionStorage.removeItem("c8_just_unlocked_player_name");
      setJustUnlockedProfile(true);
      if (justUnlockedProfileTimeoutRef.current)
        clearTimeout(justUnlockedProfileTimeoutRef.current);
      justUnlockedProfileTimeoutRef.current = setTimeout(
        () => setJustUnlockedProfile(false),
        1200
      );
    };
    window.addEventListener(
      "runProfileUnlockAnimation",
      handleRunProfileUnlockAnimation
    );
    return () => {
      window.removeEventListener(
        "runProfileUnlockAnimation",
        handleRunProfileUnlockAnimation
      );
      if (justUnlockedProfileTimeoutRef.current)
        clearTimeout(justUnlockedProfileTimeoutRef.current);
    };
  }, [user?.allow_player_name_edit]);

  const fetchCareerSummary = useCallback(async () => {
    if (!sessionToken) return;
    try {
      const res = await fetch(`${process.env.API_URL}/account/career-summary`, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        const parsed = parseCareerSummaryPayload(data);
        setCareerSummary(parsed);
        if (parsed.xp) {
          window.dispatchEvent(
            new CustomEvent("c8-xp-updated", { detail: parsed.xp })
          );
        }
      } else {
        setCareerSummary(fallbackCareerSummaryZeros());
      }
    } catch {
      setCareerSummary(fallbackCareerSummaryZeros());
    }
  }, [sessionToken]);

  const refreshFriendsStrip = useCallback(async () => {
    if (!user) return;
    try {
      const res = await fetch(`${process.env.API_URL}/account/friends`, {
        method: "GET",
        headers: getAuthHeaders(),
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data?.friends) {
        setFriendsStrip((data.friends as FriendRow[]).slice(0, 12));
      }
    } catch {
      // keep previous strip
    }
  }, [user]);

  useEffect(() => {
    fetchCareerSummary();
  }, [fetchCareerSummary]);

  useEffect(() => {
    void refreshFriendsStrip();
  }, [refreshFriendsStrip]);

  useEffect(() => {
    const viewerId = user?.userId != null ? Number(user.userId) : null;
    if (viewerId == null) {
      setOwnActivityDigests([]);
      setOwnActivityLiveFriends([]);
      setOwnActivityMyTrades([]);
      setOwnActivityMySquares([]);
      setOwnActivityHasMore(false);
      setOwnActivityLoadingMore(false);
      setOwnActivityLoading(false);
      return;
    }
    let cancelled = false;
    const hasExistingOwnActivity =
      ownActivityDigests.length > 0 ||
      ownActivityLiveFriends.length > 0 ||
      ownActivityMyTrades.length > 0 ||
      ownActivityMySquares.length > 0;
    if (!hasExistingOwnActivity) setOwnActivityLoading(true);
    else setOwnActivityLoadingMore(true);
    const actUrl = `${
      process.env.API_URL
    }/account/friends-activity?${new URLSearchParams({
      friend_id: String(viewerId),
      results_limit: String(ownActivityResultsLimit),
    })}`;
    void Promise.all([
      fetch(actUrl, { method: "GET", headers: getAuthHeaders() }),
      sessionToken ? fetchMyGamesOverview() : Promise.resolve(null),
    ])
      .then(async ([actRes, myGamesPayload]) => {
        if (cancelled) return;
        if (actRes.ok) {
          const data = (await actRes.json()) as {
            live_friend?: FriendListEntry | null;
            activity?: unknown;
            has_more?: boolean;
          };
          setOwnActivityDigests(mapDigestRows(data));
          setOwnActivityHasMore(Boolean(data.has_more));
          const lf = data.live_friend;
          if (lf && typeof lf === "object" && lf.userId != null) {
            setOwnActivityLiveFriends([lf]);
          } else {
            setOwnActivityLiveFriends([]);
          }
        } else {
          setOwnActivityDigests([]);
          setOwnActivityLiveFriends([]);
        }
        if (myGamesPayload?.active) {
          setOwnActivityMyTrades(
            Array.isArray(myGamesPayload.active.trades)
              ? myGamesPayload.active.trades
              : []
          );
          setOwnActivityMySquares(
            Array.isArray(myGamesPayload.active.squares)
              ? myGamesPayload.active.squares
              : []
          );
        } else {
          setOwnActivityMyTrades([]);
          setOwnActivityMySquares([]);
        }
        if (!cancelled) {
          setOwnActivityLoading(false);
          setOwnActivityLoadingMore(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setOwnActivityDigests([]);
          setOwnActivityLiveFriends([]);
          setOwnActivityMyTrades([]);
          setOwnActivityMySquares([]);
          setOwnActivityHasMore(false);
          setOwnActivityLoading(false);
          setOwnActivityLoadingMore(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    fetchMyGamesOverview,
    ownActivityRefreshTick,
    sessionToken,
    user?.userId,
    ownActivityResultsLimit,
    ownActivityDigests.length,
    ownActivityLiveFriends.length,
    ownActivityMyTrades.length,
    ownActivityMySquares.length,
  ]);

  useDebouncedWindowEvents(
    [
      "c8-activity-updated",
      "c8-my-games-dirty",
      "c8-friends-dirty",
      "c8-profile-dirty",
      "c8-notifications-updated",
    ] as const,
    () => {
      setOwnActivityRefreshTick((n) => n + 1);
    },
    450,
    Boolean(user?.userId)
  );

  useVisibleStaleFallback(
    () => setOwnActivityRefreshTick((n) => n + 1),
    Boolean(user?.userId)
  );

  /** Career: SSE + wallet/games signals (avoid c8-notifications-updated — too chatty). */
  useDebouncedWindowEvents(
    [
      "c8-profile-dirty",
      "walletUpdate",
      "gamesCountUpdated",
      "gamesCountUpdate",
      "c8-my-games-dirty",
    ] as const,
    () => {
      void fetchCareerSummary();
    },
    600,
    Boolean(sessionToken)
  );

  useVisibleStaleFallback(() => fetchCareerSummary(), Boolean(sessionToken));

  /** Friends strip: friend-graph changes from notification SSE → Header. */
  useDebouncedWindowEvents(
    ["c8-friends-dirty", "c8-activity-updated"] as const,
    () => {
      void refreshFriendsStrip();
    },
    750,
    Boolean(user)
  );

  useVisibleStaleFallback(() => void refreshFriendsStrip(), Boolean(user));

  useEffect(() => {
    document.title = "Player profile | Cricket8";
    return () => {
      document.title = "Cricket8";
    };
  }, []);

  if (!user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4">
        <p className="text-white/80 text-center">
          Sign in to view your profile.
        </p>
      </div>
    );
  }

  const games: UserGame[] = Array.isArray(user.games) ? user.games : [];
  const coins = user.wallet?.coins ?? 0;
  const rubies = user.wallet?.rubies ?? 0;
  const xpTotal = user.wallet?.xp ?? careerSummary?.xp?.total_xp ?? 0;
  const favourites = user.favourites ?? null;
  const playerId =
    (user.playerId ?? (user as { player_id?: string }).player_id)?.trim() ||
    undefined;
  const senderUserId =
    playerId ?? (user.userId != null ? String(user.userId) : undefined);
  const inviteUrl =
    typeof window !== "undefined" && user.userId
      ? playerId
        ? `${window.location.origin}/profile?p=${encodeURIComponent(playerId)}`
        : `${window.location.origin}/profile?u=${encodeURIComponent(
            String(user.userId)
          )}`
      : "";
  const inviteUrlDisplay = inviteUrl.replace(/^https?:\/\//i, "");

  const handleCopyInvite = useCallback(() => {
    if (!inviteUrl) return;
    trackCtaClicked({
      user_id: user?.userId,
      cta_name: "copy_profile_link",
      cta_location: "profile",
      target_url: inviteUrl,
      source_page: location.pathname,
      pathname: location.pathname,
    });
    trackInviteFlowOpened({
      user_id: user?.userId,
      entry_point: "profile_invite",
      pathname: location.pathname,
    });
    const onCopied = () => {
      trackProfileLinkShared({
        user_id: user?.userId,
        invite_method: "link",
        sender_user_id: senderUserId,
        pathname: location.pathname,
      });
      if (user?.userId != null)
        markMyZoneOnboardingMilestone(user.userId, "invite");
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 2000);
    };
    const clipboard =
      typeof navigator !== "undefined" ? navigator.clipboard : undefined;
    if (clipboard?.writeText) {
      clipboard.writeText(inviteUrl).then(onCopied, onCopied);
    } else {
      onCopied();
    }
  }, [inviteUrl, senderUserId, user?.userId, location.pathname]);

  const inviteShareDisplayName =
    String(user.player_name ?? user.username ?? "").trim() || undefined;

  const handleShareInvite = useCallback(() => {
    if (!inviteUrl || !navigator.share) return;
    trackCtaClicked({
      user_id: user?.userId,
      cta_name: "share_profile",
      cta_location: "profile",
      target_url: inviteUrl,
      source_page: location.pathname,
      pathname: location.pathname,
    });
    trackInviteFlowOpened({
      user_id: user?.userId,
      entry_point: "profile_invite",
      pathname: location.pathname,
    });
    navigator
      .share(
        nativeShareData(
          buildProfileInviteShareText(inviteShareDisplayName),
          inviteUrl,
          buildProfileInviteShareTitle(inviteShareDisplayName)
        )
      )
      .then(() => {
        trackProfileLinkShared({
          user_id: user?.userId,
          invite_method: "share_sheet",
          sender_user_id: senderUserId,
          pathname: location.pathname,
        });
        if (user?.userId != null)
          markMyZoneOnboardingMilestone(user.userId, "invite");
      })
      .catch(() => {});
  }, [
    inviteShareDisplayName,
    inviteUrl,
    senderUserId,
    user?.userId,
    location.pathname,
  ]);

  const handleStartEdit = useCallback(() => {
    if (editPageMode) return;
    navigate("/edit-profile", { state: profileModalBackgroundState(location) });
  }, [editPageMode, navigate, location]);

  const handleCancelEdit = useCallback(() => {
    setSaveFeedback(null);
    setEditPlayerNameError(null);
    if (editPageMode) {
      onLeaveEditPage?.();
      return;
    }
    setIsEditing(false);
    if (embedInGlobalModal) return;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("edit");
      return next.toString() ? next : {};
    });
  }, [editPageMode, onLeaveEditPage, embedInGlobalModal, setSearchParams]);

  const leaveEditPageAfterSave = useCallback(() => {
    if (editPageMode) {
      onLeaveEditPage?.({ openOwnProfileModal: true });
    }
  }, [editPageMode, onLeaveEditPage]);

  /** Mobile save is in a sticky footer; scroll the form panel so the failing field/message is visible. */
  useEffect(() => {
    if (!isEditing) return;
    const target = editPlayerNameError
      ? document.getElementById("profile-edit-player-name")
      : editDisplayNameError
      ? document.getElementById("profile-edit-display-name")
      : saveFeedback?.error
      ? document.getElementById("profile-edit-save-feedback")
      : null;
    if (!target) return;

    const frame = requestAnimationFrame(() => {
      const scrollParent = editFormScrollRef.current;
      if (scrollParent) {
        const targetRect = target.getBoundingClientRect();
        const parentRect = scrollParent.getBoundingClientRect();
        const delta =
          targetRect.top -
          parentRect.top -
          (parentRect.height - targetRect.height) / 2;
        scrollParent.scrollBy({ top: delta, behavior: "smooth" });
      } else {
        target.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      if (
        (editPlayerNameError || editDisplayNameError) &&
        target instanceof HTMLInputElement
      ) {
        target.focus({ preventScroll: true });
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [editPlayerNameError, editDisplayNameError, saveFeedback, isEditing]);

  const handleSaveProfile = useCallback(async () => {
    if (!user || !sessionToken || saving) return;
    setSaving(true);
    setSaveFeedback(null);
    setEditPlayerNameError(null);
    setEditDisplayNameError(null);
    const servicecomms =
      (user as { servicecomms?: boolean }).servicecomms ?? true;
    let clearSavingInFinally = true;
    try {
      const trimmedPlayerName = editPlayerName.trim();
      const trimmedDisplayName = editDisplayName.trim().replace(/\s+/g, " ");
      const handleFallback =
        trimmedPlayerName ||
        user.player_name?.trim() ||
        user.username?.trim() ||
        "";
      // Store null when empty or still equal to the handle — UI falls back to player_name.
      const displayNameToSave =
        !trimmedDisplayName ||
        trimmedDisplayName.toLowerCase() === handleFallback.toLowerCase()
          ? ""
          : trimmedDisplayName;
      const res = await fetch(`${process.env.API_URL}/account/update`, {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({
          username: user.username,
          email: user.email,
          phone: user.phone ?? "",
          usersession: sessionToken,
          marketing: user.marketing ?? false,
          servicecomms,
          country: editCountry || undefined,
          favsports: serializeFavSportsToStorage(editFavSports),
          favfootballteams: serializeFavFootballToStorage(editFavFootballSlugs),
          faviplteams: serializeFavIplToStorage(editFavIplSlugs),
          favnationalteams: serializeFavCricketIntToStorage(
            editFavCricketIntSlugs
          ),
          favcricketers: serializeFavCricketersToStorage(editFavCricketerSlugs),
          ...(user.allow_player_name_edit && {
            player_name: trimmedPlayerName || undefined,
            display_name: displayNameToSave,
          }),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && !data.error) {
        const favSportsCount = Array.isArray(editFavSports)
          ? editFavSports.length
          : 0;
        const favCricketPlayersCount = Array.isArray(editFavCricketerSlugs)
          ? editFavCricketerSlugs.length
          : 0;
        const favFootballCount = Array.isArray(editFavFootballSlugs)
          ? editFavFootballSlugs.length
          : 0;
        const favIplCount = Array.isArray(editFavIplSlugs)
          ? editFavIplSlugs.length
          : 0;
        const favCricketIntCount = Array.isArray(editFavCricketIntSlugs)
          ? editFavCricketIntSlugs.length
          : 0;
        const updatedFields = [];
        if (editCountry) updatedFields.push("country");
        if (favSportsCount > 0) updatedFields.push("favorite_sports");
        if (favCricketPlayersCount > 0)
          updatedFields.push("favorite_cricket_players");
        if (favIplCount > 0 || favCricketIntCount > 0)
          updatedFields.push("favorite_cricket_teams");
        if (favFootballCount > 0) updatedFields.push("favorite_football_teams");
        if (user.allow_player_name_edit && trimmedPlayerName)
          updatedFields.push("player_name");
        if (user.allow_player_name_edit && displayNameToSave)
          updatedFields.push("display_name");
        trackProfileUpdated({
          user_id: user.userId,
          updated_fields: updatedFields,
          country: editCountry || undefined,
          favorite_sports_count: favSportsCount,
          favorite_cricket_players_count: favCricketPlayersCount,
          favorite_teams_count:
            favFootballCount + favIplCount + favCricketIntCount,
        });
        const verifyUrl = getVerifySessionUrl();
        let refreshedUser: User | null = null;
        if (verifyUrl) {
          const verifyRes = await fetch(verifyUrl, {
            method: "GET",
            headers: getAuthHeaders(),
          });
          if (verifyRes.ok) {
            const updated = (await verifyRes.json()) as Record<string, unknown>;
            refreshedUser = normalizeSessionPayload(updated) as User;
          }
        }
        if (refreshedUser) {
          const nextUser = refreshedUser;
          setUser((prev) => {
            if (!prev) return nextUser;
            const nextSrc = resolveAvatarUrl(nextUser);
            const prevSrc = resolveAvatarUrl(prev);
            if (nextSrc) return nextUser;
            if (prevSrc) {
              return {
                ...nextUser,
                avatar: prev.avatar,
                avatar_url: prev.avatar_url ?? nextUser.avatar_url ?? null,
                original_avatar:
                  nextUser.original_avatar ?? prev.original_avatar ?? null,
                avatar_key: nextUser.avatar_key ?? prev.avatar_key ?? null,
              };
            }
            return nextUser;
          });
          if (user.userId != null)
            markMyZoneOnboardingMilestone(user.userId, "profile");
        }
        if (editPageMode) {
          clearSavingInFinally = false;
          setSaving(false);
          leaveEditPageAfterSave();
          return;
        }
        setSaveFeedback({ message: data.message || "Profile updated!" });
        setTimeout(() => {
          setSaveFeedback(null);
          setIsEditing(false);
          if (!embedInGlobalModal) {
            setSearchParams((prev) => {
              const next = new URLSearchParams(prev);
              next.delete("edit");
              return next.toString() ? next : {};
            });
          }
        }, 1500);
      } else {
        const msg =
          (typeof data.message === "string" && data.message.trim()) ||
          "Something went wrong.";
        if (isDisplayNameFieldApiError(data)) {
          setEditDisplayNameError(msg);
        } else if (isPlayerNameFieldApiError(data)) {
          setEditPlayerNameError(
            isUsernameAlreadyTakenApiError(data)
              ? PLAYER_NAME_ALREADY_TAKEN_MESSAGE
              : msg
          );
        } else {
          setSaveFeedback({ message: msg, error: true });
        }
      }
    } catch {
      setSaveFeedback({ message: "Something went wrong.", error: true });
    } finally {
      if (clearSavingInFinally) {
        setSaving(false);
      }
    }
  }, [
    user,
    sessionToken,
    saving,
    editCountry,
    editFavSports,
    editFavFootballSlugs,
    editFavIplSlugs,
    editFavCricketIntSlugs,
    editFavCricketerSlugs,
    editPlayerName,
    editDisplayName,
    setUser,
    setSearchParams,
    embedInGlobalModal,
    editPageMode,
    leaveEditPageAfterSave,
  ]);

  const [hasAvatarFrames, setHasAvatarFrames] = useState(false);
  useEffect(() => {
    if (!user?.userId || user.allow_player_name_edit) return undefined;
    let cancelled = false;
    fetch(`${process.env.API_URL}/account/avatar-frames`, {
      headers: getAuthHeaders(),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!cancelled)
          setHasAvatarFrames(
            (Array.isArray(data?.frames) && data.frames.length > 0) ||
              (Array.isArray(data?.shopFrames) && data.shopFrames.length > 0)
          );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [user?.userId, user?.allow_player_name_edit]);

  const openAvatarChooser = useCallback((presetKey?: string) => {
    setAvatarChooserInitialKey(presetKey !== undefined ? presetKey : undefined);
    setAvatarChooserOpen(true);
  }, []);

  const closeAvatarChooser = useCallback(() => {
    setAvatarChooserOpen(false);
    setAvatarChooserInitialKey(undefined);
  }, []);

  const canNativeShare = typeof navigator !== "undefined" && !!navigator.share;

  const formatCoins = (amount: number) =>
    new Intl.NumberFormat("en-IN").format(Math.round(amount));

  const profileLinkBackground: ProfileModalLocationState = {
    from: (location.state as ProfileModalLocationState | null)?.from ?? {
      pathname: location.pathname,
      search: location.search,
    },
  };

  const joinDateShort = user.created_at
    ? new Date(user.created_at).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    : null;

  const legacyMiniGamesPlayed = games.reduce((sum, g) => {
    if (g.name !== "quickfire-trivia") return sum;
    const p = g.values?.played;
    const n = typeof p === "number" ? p : Number(p);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);

  const legacyCoinsWonHeader =
    games.reduce((sum, g) => {
      const w = g.values?.lastwon;
      const n = typeof w === "number" ? w : Number(w);
      if (g.name === "quickfire-trivia") {
        return sum + (Number.isFinite(n) ? n : 0);
      }
      return sum;
    }, 0) +
    (careerSummary?.trader.profit ?? 0) +
    (careerSummary?.squares.net_winnings ?? 0) +
    (careerSummary?.bullion.net_winnings ?? 0) +
    (careerSummary?.sixes.net_winnings ?? 0) +
    (careerSummary?.leagues.net_winnings ?? 0);

  const totalGamesPlayed =
    careerSummary != null
      ? careerSummary.totals.games_played
      : legacyMiniGamesPlayed;

  const coinsWonDisplay =
    careerSummary != null
      ? careerSummary.totals.net_coins_won
      : legacyCoinsWonHeader;

  const favChips: ProfileFavChip[] = [];
  for (const label of parseFavSportsFromStorage(favourites?.favsports)) {
    favChips.push({ key: `sport-${label}`, label });
  }
  for (const slug of parseFavCricketersFromStorage(favourites?.favcricketers)) {
    favChips.push({ key: `cr-${slug}`, label: favCricketerLabel(slug) });
  }
  const iplStoreSlugs = parseFavIplFromStorage(favourites?.faviplteams);
  const nationalStoreSlugs = parseFavCricketIntFromStorage(
    favourites?.favnationalteams
  );
  const pickerTeamSlugs = new Set<string>(
    [...iplStoreSlugs, ...nationalStoreSlugs].map(canonicalTeamSlug)
  );
  for (const slug of iplStoreSlugs) {
    favChips.push({
      key: `ipl-${slug}`,
      label: favIplLabel(slug),
      longLabel: favIplLongLabel(slug),
    });
  }
  for (const slug of nationalStoreSlugs) {
    favChips.push({ key: `cint-${slug}`, label: favCricketIntLabel(slug) });
  }
  for (const name of parseFavTeamsFromStorage(
    favourites?.favcustomcricketteams
  )) {
    if (pickerTeamSlugs.has(canonicalTeamSlug(name))) continue;
    favChips.push({
      key: `team-${slugifyTeamName(name)}`,
      label: name,
      longLabel: name,
    });
  }
  for (const slug of parseFavFootballFromStorage(
    favourites?.favfootballteams
  )) {
    favChips.push({
      key: `fb-${slug}`,
      label: favFootballLabel(slug),
      longLabel: favFootballLongLabel(slug),
    });
  }

  const playerNameLabel = playerHandleName(user);
  const realNameLabel = optionalDisplayName(user);
  const showOnlineBadge = showOnlineOnOwnProfile(user);

  /** Unlock next to player name / name: fixed 43×21, spec typography (no room for ruby chip). */
  const renderProfileUsernameUnlockButton = () => (
    <button
      type="button"
      onClick={openShopDrawer}
      className={profileEditUsernameUnlockButtonClass}
      aria-label="Unlock profile features in shop"
    >
      Unlock
    </button>
  );

  const renderSaveProfileButton = () => (
    <button
      type="submit"
      form="profile-edit-form"
      disabled={saving}
      className="relative flex h-[40px] min-h-[40px] max-h-[40px] w-full min-w-0 items-center justify-center rounded-none border-0 bg-[#D2FF00] px-10 text-center font-anton text-[16px] font-normal not-italic uppercase leading-[100%] tracking-normal text-[#1b062f] transition-[filter] [leading-trim:none] hover:brightness-95 disabled:opacity-60"
    >
      <span className="text-center">{saving ? "Saving…" : "Save profile"}</span>
      <img
        src="/assets/arrow-right.svg"
        alt=""
        width={22}
        height={22}
        className="pointer-events-none absolute right-3 top-1/2 h-[22px] w-[22px] -translate-y-1/2 object-contain sm:right-4"
        aria-hidden
      />
    </button>
  );

  const ownProfileDisplayName = playerNameLabel;
  const ownRawPhotoUrl = resolveAvatarUrl(user);
  const ownResolvedHeroSrc = toProfileHeroAvatarUrl(ownRawPhotoUrl);
  const ownRawHadPhoto = Boolean(
    ownRawPhotoUrl && String(ownRawPhotoUrl).trim()
  );

  return (
    <div
      className={`flex w-full flex-col ${
        editPageMode
          ? "h-full min-h-0 flex-1 overflow-hidden bg-[#1B062F] md:h-auto md:flex-none md:overflow-visible"
          : "h-full min-h-0 flex-1 overflow-hidden"
      }`}
    >
      {isEditing ? (
        <header
          className={`grid shrink-0 grid-cols-[auto_1fr_auto] items-center gap-2 border-b border-black/15 bg-gradient-to-r from-[#652DAF] to-[#623998] ${
            editPageMode
              ? "w-full min-h-[calc(3.125rem+env(safe-area-inset-top,0px))] pt-[env(safe-area-inset-top,0px)] px-4 sm:px-5 md:h-[97px] md:min-h-[97px] md:max-h-[97px] md:px-14 md:pt-0"
              : "h-[50px] min-h-[50px] max-h-[50px] px-3"
          }`}
        >
          {editPageMode ? (
            <button
              type="button"
              onClick={() => onLeaveEditPage?.()}
              className="flex shrink-0 items-center border-0 bg-transparent p-0"
              aria-label="Back"
            >
              <img
                src={CRICKET8_LOGO_SRC}
                alt=""
                className="h-[34px] w-[34px] object-contain md:h-[74px] md:w-[74px]"
                width={74}
                height={74}
              />
            </button>
          ) : (
            <Link
              to="/"
              className="flex shrink-0 items-center"
              aria-label="Home"
            >
              <img
                src={CRICKET8_LOGO_SRC}
                alt=""
                className="h-[34px] w-[34px] object-contain"
                width={34}
                height={34}
              />
            </Link>
          )}
          <h1
            className={`min-w-0 truncate text-center font-anton font-normal uppercase tracking-normal text-white [leading-trim:none] ${
              editPageMode
                ? "text-base leading-none sm:text-lg md:text-[20px] md:leading-[100%]"
                : "text-sm"
            }`}
          >
            Edit profile
          </h1>
          <button
            type="button"
            onClick={handleCancelEdit}
            className={`shrink-0 border-0 bg-transparent px-1 py-2 text-white hover:opacity-90 ${profileEditCancelButtonClass}`}
          >
            Cancel
          </button>
        </header>
      ) : (
        <div className="shrink-0 border-b border-white/10 px-3 pb-1 pt-1 sm:pt-1.5">
          <div className="grid grid-cols-[1fr_auto_1fr] items-start gap-x-2">
            <div className="flex min-w-0 flex-col items-start gap-2">
              <span
                className={`flex items-center gap-1.5 font-anton text-[8px] font-normal uppercase leading-[12px] tracking-normal [leading-trim:none] ${
                  showOnlineBadge ? "text-[#D2FF00]" : "text-white/55"
                }`}
              >
                <span
                  className={`h-[7px] w-[7px] shrink-0 rounded-full ${
                    showOnlineBadge ? "bg-[#D2FF00]" : "bg-white/45"
                  }`}
                  aria-hidden
                />
                {showOnlineBadge ? "Online" : "Offline"}
              </span>
              <button
                type="button"
                onClick={handleStartEdit}
                className="h-8 shrink-0 rounded-none bg-[#D2FF00] px-3 font-anton text-[10px] font-normal uppercase tracking-wide text-black hover:brightness-95"
              >
                Edit profile
              </button>
            </div>
            <div className="col-start-2 flex max-w-[min(100%,11rem)] flex-col items-center gap-2 justify-self-center sm:max-w-[13rem]">
              <motion.div
                className="relative shrink-0 rounded-full"
                initial={false}
                animate={
                  justUnlockedProfile
                    ? {
                        boxShadow: [
                          "0 0 0 transparent",
                          "0 0 22px rgba(210,255,0,0.55)",
                          "0 0 0 transparent",
                        ],
                        scale: [1, 1.06, 1],
                      }
                    : { boxShadow: "0 0 0 transparent", scale: 1 }
                }
                transition={
                  justUnlockedProfile
                    ? {
                        duration: 0.9,
                        times: [0, 0.35, 1],
                        ease: "easeOut",
                      }
                    : { duration: 0 }
                }
              >
                <ProfileAvatarHeroWithFallback
                  resolvedSrc={ownResolvedHeroSrc}
                  rawHadPhoto={ownRawHadPhoto}
                  displayName={ownProfileDisplayName}
                  xpWeekCrown={user.xp_week_crown}
                  avatarFrameUrl={user.avatar_frame_url}
                  avatarFrameSmallUrl={user.avatar_frame_small_url}
                />
                {ownProfileHeroCountryFlag ? (
                  <span
                    className="pointer-events-none absolute -right-2 -top-2 z-[1] select-none text-[15px] leading-none"
                    aria-hidden
                  >
                    {ownProfileHeroCountryFlag}
                  </span>
                ) : null}
              </motion.div>
              <ProfileNameStack
                className="w-full max-w-full"
                playerName={playerNameLabel}
                displayName={realNameLabel}
                renderPlayerName={(label) => (
                  <motion.button
                    type="button"
                    onClick={() =>
                      navigate("/account", { state: location.state })
                    }
                    className="max-w-full min-w-0 truncate text-center font-anton text-[20px] font-normal uppercase leading-[26px] tracking-normal text-white [leading-trim:none] hover:text-lime-400"
                    initial={false}
                    animate={
                      justUnlockedProfile
                        ? {
                            color: ["#ffffff", "#D2FF00", "#ffffff"],
                            scale: [1, 1.04, 1],
                          }
                        : { color: "#ffffff", scale: 1 }
                    }
                    transition={
                      justUnlockedProfile
                        ? {
                            duration: 0.75,
                            times: [0, 0.4, 1],
                            ease: "easeOut",
                          }
                        : { duration: 0 }
                    }
                  >
                    {label.toUpperCase()}
                  </motion.button>
                )}
              />
            </div>
            <div className="col-start-3 justify-self-end self-start">
              {onClose ? (
                <ModalCloseButton
                  onClick={onClose}
                  ariaLabel="Close profile"
                  className="h-11 w-11 shrink-0 rounded-none hover:bg-white/10 hover:opacity-100"
                />
              ) : null}
            </div>
          </div>
        </div>
      )}

      {isEditing ? (
        <div
          className={
            editPageMode
              ? "flex min-h-0 w-full flex-1 flex-col overflow-hidden md:flex-none md:overflow-visible"
              : "flex min-h-0 flex-1 flex-col overflow-hidden"
          }
        >
          <div
            ref={editFormScrollRef}
            className={
              editPageMode
                ? "min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pt-4 sm:px-6 md:overflow-visible"
                : "min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-2 pt-3"
            }
          >
            <div
              className={
                editPageMode
                  ? "mx-auto w-full max-w-[630px] pb-4 md:pb-32"
                  : "contents"
              }
            >
              {user.is_guest ? (
                <section className="mb-2 w-full bg-[#652DAF] px-3 py-3">
                  <p className="font-anton text-[16px] uppercase text-white">
                    Guest account
                  </p>
                  <p className="mt-1 font-chakra text-[12px] normal-case leading-[16px] text-white/80">
                    <a
                      href="/signup?back=%2Fprofile"
                      className="text-lime underline"
                    >
                      Sign in with Google, Facebook or phone
                    </a>{" "}
                    to keep your winnings for good.
                  </p>
                </section>
              ) : null}
              {user.allow_player_name_edit !== true && (
                <ProfileUnlockBanner variant="edit" />
              )}

              <section className="mb-0 w-full min-w-0 overflow-x-hidden">
                <form
                  id="profile-edit-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleSaveProfile();
                  }}
                  className="flex w-full min-w-0 flex-col gap-2"
                >
                  <section className="min-w-0 w-full overflow-hidden rounded-none bg-[#652DAF] px-1.5 py-0 sm:px-2">
                    <div className={profileEditFormRowFlexClass}>
                      <span
                        className={`${profileEditFormLabelCellClass} ${profileEditFormTopFieldLabelMinClass} ${profileEditFormLabelNowrapClass} ${profileEditRowLabelClass}`}
                      >
                        Player name
                      </span>
                      <div className={profileEditFormMiddleCellClass}>
                        <div
                          className={`box-border flex h-[30px] w-full min-w-0 items-center gap-1 border border-white/20 pl-1 pr-1.5 ${profileEditFieldBgClass} ${
                            !user.allow_player_name_edit
                              ? "cursor-not-allowed"
                              : ""
                          }`}
                        >
                          <input
                            id="profile-edit-player-name"
                            type="text"
                            value={editPlayerName}
                            onChange={
                              user.allow_player_name_edit
                                ? (e) => {
                                    setEditPlayerName(e.target.value);
                                    setEditPlayerNameError(null);
                                  }
                                : undefined
                            }
                            readOnly={!user.allow_player_name_edit}
                            disabled={!user.allow_player_name_edit}
                            placeholder="Player name"
                            aria-label="Player name"
                            aria-invalid={
                              editPlayerNameError ? true : undefined
                            }
                            aria-describedby={
                              editPlayerNameError
                                ? "profile-edit-player-name-error"
                                : undefined
                            }
                            className={`h-full min-h-0 min-w-0 flex-1 bg-transparent py-0 outline-none placeholder:text-white/45 ${profileEditUsernameInputClass} ${
                              user.allow_player_name_edit
                                ? "text-white"
                                : "cursor-not-allowed text-white/55 placeholder:text-white/35"
                            }`}
                          />
                          {!user.allow_player_name_edit && (
                            <span
                              className="shrink-0 text-sm leading-none opacity-90"
                              aria-hidden
                              title="Locked"
                            >
                              🔒
                            </span>
                          )}
                        </div>
                      </div>
                      <div className={profileEditFormRailCellClass}>
                        {!user.allow_player_name_edit ? (
                          renderProfileUsernameUnlockButton()
                        ) : (
                          <span
                            className="block h-[21px] w-[43px] shrink-0"
                            aria-hidden
                          />
                        )}
                      </div>
                    </div>
                    {editPlayerNameError ? (
                      <div className="flex w-full min-w-0 flex-nowrap items-start gap-1.5 pb-1">
                        <span
                          className={`${profileEditFormLabelCellClass} ${profileEditFormTopFieldLabelMinClass} shrink-0`}
                          aria-hidden
                        />
                        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
                          <p
                            id="profile-edit-player-name-error"
                            className="font-chakra text-[12px] font-normal leading-[16px] [leading-trim:none]"
                            style={{ color: "red" }}
                            role="alert"
                          >
                            {editPlayerNameError}
                          </p>
                        </div>
                        <div
                          className="w-[43px] min-w-[43px] max-w-[43px] shrink-0"
                          aria-hidden
                        />
                      </div>
                    ) : null}
                  </section>

                  <section className="min-w-0 w-full overflow-hidden rounded-none bg-[#652DAF] px-1.5 py-0 sm:px-2">
                    <div className={profileEditFormRowFlexClass}>
                      <span
                        className={`${profileEditFormLabelCellClass} ${profileEditFormTopFieldLabelMinClass} ${profileEditFormLabelNowrapClass} ${profileEditRowLabelClass}`}
                      >
                        Your name
                      </span>
                      <div className={profileEditFormMiddleCellClass}>
                        <div
                          className={`box-border flex h-[30px] w-full min-w-0 items-center gap-1 border border-white/20 pl-1 pr-1.5 ${profileEditFieldBgClass} ${
                            !user.allow_player_name_edit
                              ? "cursor-not-allowed"
                              : ""
                          }`}
                        >
                          <input
                            id="profile-edit-display-name"
                            type="text"
                            value={editDisplayName}
                            onChange={
                              user.allow_player_name_edit
                                ? (e) => {
                                    setEditDisplayName(e.target.value);
                                    setEditDisplayNameError(null);
                                  }
                                : undefined
                            }
                            readOnly={!user.allow_player_name_edit}
                            disabled={!user.allow_player_name_edit}
                            placeholder="Optional — shown to friends"
                            aria-label="Your name"
                            aria-invalid={
                              editDisplayNameError ? true : undefined
                            }
                            aria-describedby={
                              editDisplayNameError
                                ? "profile-edit-display-name-error"
                                : undefined
                            }
                            className={`h-full min-h-0 min-w-0 flex-1 bg-transparent py-0 outline-none placeholder:text-white/45 ${profileEditUsernameInputClass} ${
                              user.allow_player_name_edit
                                ? "text-white"
                                : "cursor-not-allowed text-white/55 placeholder:text-white/35"
                            }`}
                          />
                          {!user.allow_player_name_edit && (
                            <span
                              className="shrink-0 text-sm leading-none opacity-90"
                              aria-hidden
                              title="Locked"
                            >
                              🔒
                            </span>
                          )}
                        </div>
                      </div>
                      <div className={profileEditFormRailCellClass}>
                        <span
                          className="block h-[21px] w-[43px] shrink-0"
                          aria-hidden
                        />
                      </div>
                    </div>
                    {editDisplayNameError ? (
                      <div className="flex w-full min-w-0 flex-nowrap items-start gap-1.5 pb-1">
                        <span
                          className={`${profileEditFormLabelCellClass} ${profileEditFormTopFieldLabelMinClass} shrink-0`}
                          aria-hidden
                        />
                        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
                          <p
                            id="profile-edit-display-name-error"
                            className="font-chakra text-[12px] font-normal leading-[16px] [leading-trim:none]"
                            style={{ color: "red" }}
                            role="alert"
                          >
                            {editDisplayNameError}
                          </p>
                        </div>
                        <div
                          className="w-[43px] min-w-[43px] max-w-[43px] shrink-0"
                          aria-hidden
                        />
                      </div>
                    ) : null}
                  </section>

                  <section className="min-w-0 w-full overflow-hidden rounded-none bg-[#652DAF] px-1.5 py-0 sm:px-2">
                    <div className={profileEditFormRowFlexClass}>
                      <span
                        className={`${profileEditFormLabelCellClass} ${profileEditFormTopFieldLabelMinClass} ${profileEditFormLabelNowrapClass} ${profileEditRowLabelClass}`}
                      >
                        Avatar
                      </span>
                      <div className={profileEditFormMiddleCellClass}>
                        <div className="flex h-full w-full min-w-0 flex-1 items-center gap-1 overflow-hidden sm:gap-2">
                          {user.allow_player_name_edit ? (
                            <>
                              <button
                                type="button"
                                className={`relative box-border h-[30px] w-[30px] shrink-0 overflow-hidden border border-white/20 p-0 ${profileEditFieldBgClass} cursor-pointer hover:brightness-110`}
                                title="Your current avatar"
                                aria-label="Open avatar chooser"
                                onClick={() => openAvatarChooser()}
                              >
                                <UserAvatar
                                  src={resolveAvatarUrl(user) || user.avatar}
                                  displayName={ownProfileDisplayName}
                                  size="sm"
                                  withCrown={false}
                                />
                              </button>
                              <button
                                type="button"
                                onClick={() => openAvatarChooser()}
                                className="inline-flex h-[30px] min-h-[30px] max-h-[30px] shrink-0 items-center justify-center bg-[#d2ff00] px-2 font-anton text-[11px] font-normal uppercase tracking-wide text-[#1b062f] hover:brightness-95 sm:px-3 sm:text-[12px]"
                              >
                                Change
                              </button>
                            </>
                          ) : (
                            <div
                              className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto overflow-y-hidden sm:gap-1.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                              role="list"
                              aria-label="Avatar choices"
                            >
                              <button
                                type="button"
                                className={`relative box-border h-[30px] w-[30px] shrink-0 overflow-hidden border border-white/20 p-0 ${profileEditFieldBgClass} cursor-pointer opacity-80`}
                                role="listitem"
                                title="Your current avatar"
                                aria-label={
                                  hasAvatarFrames
                                    ? "Choose avatar frame"
                                    : "Unlock to change avatar"
                                }
                                onClick={() =>
                                  hasAvatarFrames
                                    ? openAvatarChooser()
                                    : openShopDrawer()
                                }
                              >
                                <UserAvatar
                                  src={resolveAvatarUrl(user) || user.avatar}
                                  displayName={ownProfileDisplayName}
                                  size="sm"
                                  withCrown={false}
                                />
                              </button>
                              {hasAvatarFrames ? (
                                <button
                                  type="button"
                                  role="listitem"
                                  onClick={() => openAvatarChooser()}
                                  className="inline-flex h-[30px] min-h-[30px] max-h-[30px] shrink-0 items-center justify-center bg-[#d2ff00] px-2 font-anton text-[11px] font-normal uppercase tracking-wide text-[#1b062f] hover:brightness-95 sm:px-3 sm:text-[12px]"
                                >
                                  Frame
                                </button>
                              ) : null}
                              {animalPreviewAvatars.map((opt) => {
                                const thumb = getAvatarThumbSrc(opt);
                                if (!thumb) return null;
                                const currentAvatarKey =
                                  user.avatar_key != null
                                    ? String(user.avatar_key).trim()
                                    : "";
                                const selected = currentAvatarKey === opt.key;
                                const tileClass = `relative box-border h-[30px] w-[30px] shrink-0 overflow-hidden border border-white/20 ${profileEditFieldBgClass} ${
                                  selected
                                    ? "ring-2 ring-[#d2ff00] ring-inset"
                                    : ""
                                } cursor-pointer opacity-55`;
                                return (
                                  <button
                                    key={opt.key}
                                    type="button"
                                    role="listitem"
                                    title={
                                      opt.animal
                                        ? `${opt.animal} preset`
                                        : "Preset avatar"
                                    }
                                    aria-label="Unlock to change avatar"
                                    aria-pressed={selected}
                                    className={tileClass}
                                    onClick={() => openShopDrawer()}
                                  >
                                    <img
                                      src={thumb}
                                      alt=""
                                      className="h-full w-full object-cover object-center opacity-60"
                                      width={30}
                                      height={30}
                                      decoding="async"
                                    />
                                    <span
                                      className="pointer-events-none absolute bottom-0 right-0 pb-px pr-px text-[10px] leading-none drop-shadow-[0_1px_2px_rgba(0,0,0,0.85)]"
                                      aria-hidden
                                    >
                                      🔒
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className={profileEditFormRailCellClass}>
                        {!user.allow_player_name_edit ? (
                          renderProfileUsernameUnlockButton()
                        ) : (
                          <span
                            className="block h-[21px] w-[43px] shrink-0"
                            aria-hidden
                          />
                        )}
                      </div>
                    </div>
                  </section>

                  <section className="min-w-0 w-full overflow-hidden rounded-none bg-[#652DAF] px-1.5 py-0 sm:px-2">
                    <div className={profileEditFormRowFlexClass}>
                      <span
                        className={`${profileEditFormLabelCellClass} ${profileEditFormTopFieldLabelMinClass} ${profileEditFormLabelNowrapClass} ${profileEditRowLabelClass}`}
                      >
                        Country
                      </span>
                      <div className={profileEditFormMiddleCellClass}>
                        <div
                          className={`relative box-border flex h-[30px] w-full min-w-0 flex-row flex-nowrap items-center gap-1 overflow-hidden border border-white/20 pl-1 pr-1.5 sm:gap-2 ${profileEditFieldBgClass}`}
                        >
                          <ProfileCountryCombobox
                            id="profile-edit-country"
                            value={editCountry}
                            onChange={setEditCountry}
                          />
                        </div>
                      </div>
                    </div>
                  </section>

                  <section className="min-w-0 w-full overflow-hidden rounded-none bg-[#652DAF] px-2 py-3 sm:px-4 sm:py-3.5">
                    <ProfileFavouriteSportsGrid
                      selected={editFavSports}
                      onToggle={(sport: ProfileFavSportOption) => {
                        setEditFavSports((prev) =>
                          prev.includes(sport)
                            ? prev.filter((s) => s !== sport)
                            : [...prev, sport]
                        );
                      }}
                    />
                  </section>
                  <section className="min-w-0 w-full overflow-hidden rounded-none bg-[#652DAF] px-1.5 py-3 sm:px-2 sm:py-3.5">
                    <ProfileFavouriteCricketersGrid
                      selected={editFavCricketerSlugs}
                      onToggle={(slug: ProfileFavCricketerSlug) => {
                        setEditFavCricketerSlugs((prev) =>
                          prev.includes(slug)
                            ? prev.filter((s) => s !== slug)
                            : [...prev, slug]
                        );
                      }}
                    />
                  </section>
                  <section className="min-w-0 w-full overflow-hidden rounded-none bg-[#652DAF] px-1.5 py-3 sm:px-2 sm:py-3.5">
                    <ProfileFavouriteCricketTeamsGrid
                      selectedIpl={editFavIplSlugs}
                      selectedInt={editFavCricketIntSlugs}
                      onToggleIpl={(slug: ProfileFavIplSlug) => {
                        setEditFavIplSlugs((prev) =>
                          prev.includes(slug)
                            ? prev.filter((s) => s !== slug)
                            : [...prev, slug]
                        );
                      }}
                      onToggleInt={(slug: ProfileFavCricketIntSlug) => {
                        setEditFavCricketIntSlugs((prev) =>
                          prev.includes(slug)
                            ? prev.filter((s) => s !== slug)
                            : [...prev, slug]
                        );
                      }}
                      clubExtras={clubExtras}
                      intExtras={intExtras}
                      onRemoveExtra={(teamName) => {
                        void toggleFavouriteTeam(teamName);
                      }}
                    />
                  </section>
                  <section className="min-w-0 w-full overflow-hidden rounded-none bg-[#652DAF] px-1.5 py-3 sm:px-2 sm:py-3.5">
                    <ProfileFavouriteFootballGrid
                      selected={editFavFootballSlugs}
                      onToggle={(slug: ProfileFavFootballSlug) => {
                        setEditFavFootballSlugs((prev) =>
                          prev.includes(slug)
                            ? prev.filter((s) => s !== slug)
                            : [...prev, slug]
                        );
                      }}
                    />
                  </section>
                  {saveFeedback && (
                    <p
                      id="profile-edit-save-feedback"
                      role={saveFeedback.error ? "alert" : undefined}
                      className={`text-sm ${
                        saveFeedback.error ? "text-[#f87171]" : "text-emerald-400"
                      }`}
                    >
                      {saveFeedback.message}
                    </p>
                  )}
                  {editPageMode ? (
                    <div className="mt-2 hidden w-full md:mt-10 md:block">
                      {renderSaveProfileButton()}
                    </div>
                  ) : null}
                </form>
              </section>
            </div>
          </div>
          <div
            className={`relative z-10 w-full shrink-0 bg-white px-2 py-2 pb-[calc(0.5rem+env(safe-area-inset-bottom,0px))] shadow-[0_-6px_16px_rgba(0,0,0,0.22)] sm:px-2.5 sm:py-2.5 ${
              editPageMode ? "md:hidden" : ""
            }`}
          >
            {renderSaveProfileButton()}
          </div>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 pb-8 pt-1">
          {!isEditing && (
            <div className="relative mb-3 flex flex-col items-center">
              <div className="mx-auto mt-0.5 flex items-center justify-center gap-1">
                <button
                  type="button"
                  onClick={openShopDrawer}
                  className="flex items-center justify-center gap-1 rounded-none border-0 bg-transparent p-0 transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/50"
                  aria-label="Open shop"
                >
                  <div className="flex shrink-0 items-center justify-center gap-1 rounded-none bg-[#381663] px-2 py-2">
                    <img
                      src="/assets/currency-ruby.png"
                      alt=""
                      className="h-4 w-4 shrink-0 object-contain"
                    />
                    <span className="font-anton text-[10px] font-normal leading-[100%] tracking-normal text-white [leading-trim:none]">
                      {rubies}
                    </span>
                  </div>
                  <div className="flex shrink-0 items-center justify-center gap-1 rounded-none bg-[#381663] px-2 py-2">
                    <img
                      src="/assets/currency-gold-ball.png"
                      alt=""
                      className="h-4 w-4 shrink-0 object-contain"
                    />
                    <span className="font-anton text-[10px] font-normal leading-[100%] tracking-normal text-white [leading-trim:none]">
                      {formatCoins(coins)}
                    </span>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    trackCtaClicked({
                      cta_name: "xp_history",
                      cta_location: "profile_balance_strip",
                      source_page: location.pathname,
                      pathname: location.pathname,
                    });
                    setXpHubOpen(true);
                  }}
                  className="flex shrink-0 items-center justify-center gap-1 rounded-none border-0 bg-[#381663] px-2 py-2 transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/50"
                  aria-label="XP history"
                >
                  <img
                    src="/assets/xp.webp"
                    alt=""
                    className="h-4 w-4 shrink-0 object-contain"
                    aria-hidden
                  />
                  <span className="font-anton text-[10px] font-normal leading-[100%] tracking-normal text-white tabular-nums [leading-trim:none]">
                    {formatCoins(xpTotal)}
                  </span>
                </button>
              </div>
            </div>
          )}

          {!isEditing && user.allow_player_name_edit !== true && (
            <ProfileUnlockBanner />
          )}

          {!isEditing && (
            <div
              className={`mb-3 grid grid-cols-3 gap-2 text-center ${
                user.allow_player_name_edit !== true ? "mt-1 sm:mt-1.5" : ""
              }`}
            >
              <div className="rounded-none bg-[#652DAF] px-1 py-2">
                <p className="font-anton text-[11px] font-normal uppercase leading-[12px] tracking-normal text-white/40 [leading-trim:none]">
                  Joined
                </p>
                <p className="mt-1.5 font-anton text-[15px] font-normal uppercase leading-[12px] tracking-normal text-white [leading-trim:none]">
                  {joinDateShort ?? "—"}
                </p>
              </div>
              <div className="rounded-none bg-[#652DAF] px-1 py-2">
                <p className="font-anton text-[11px] font-normal uppercase leading-[12px] tracking-normal text-white/40 [leading-trim:none]">
                  Games played
                </p>
                <p className="mt-1.5 font-anton text-[15px] font-normal uppercase leading-[12px] tracking-normal text-white [leading-trim:none]">
                  {formatCoins(totalGamesPlayed)}
                </p>
              </div>
              <div className="rounded-none bg-[#652DAF] px-1 py-2">
                <p className="font-anton text-[11px] font-normal uppercase leading-[12px] tracking-normal text-white/40 [leading-trim:none]">
                  Game score
                </p>
                <p className="mt-1.5 flex items-center justify-center gap-1 font-anton text-[15px] font-normal uppercase leading-[12px] tracking-normal text-white [leading-trim:none]">
                  <img
                    src="/assets/currency-gold-ball.png"
                    alt=""
                    className="h-3.5 w-3.5 shrink-0"
                  />
                  {formatCoins(coinsWonDisplay)}
                </p>
              </div>
            </div>
          )}

          {!isEditing && favChips.length > 0 && (
            <ProfileFavouritesStrip chips={favChips} />
          )}

          {!isEditing && careerSummary && (
            <CareerStatsStack
              xp={careerSummary.xp ?? null}
              trader={{
                played: careerSummary.trader.trades,
                won: careerSummary.trader.won,
                totalWinnings: careerSummary.trader.total_winnings,
                currentStreak: careerSummary.trader.current_win_streak,
                bestStreak: careerSummary.trader.record_win_streak,
                biggestWin: careerSummary.trader.biggest_win,
              }}
              squares={{
                played: careerSummary.squares.played,
                won: careerSummary.squares.won,
                totalWinnings: careerSummary.squares.net_winnings,
              }}
              bullion={{
                played: careerSummary.bullion.played,
                won: careerSummary.bullion.won,
                totalWinnings: careerSummary.bullion.net_winnings,
              }}
              sixes={{
                played: careerSummary.sixes.played,
                totalWinnings: careerSummary.sixes.net_winnings,
                biggestWin: careerSummary.sixes.biggest_win,
              }}
              leagues={{
                played: careerSummary.leagues.played,
                won: careerSummary.leagues.won,
                totalWinnings: careerSummary.leagues.net_winnings,
              }}
              quickfire={{
                plays: careerSummary.mini_games["quickfire-trivia"].played,
                correct:
                  careerSummary.mini_games["quickfire-trivia"].correct_answers,
                totalWinnings:
                  careerSummary.mini_games["quickfire-trivia"].net_coins,
              }}
              formatCoins={formatCoins}
            />
          )}

          {!isEditing && friendsStrip.length > 0 && (
            <section className="mb-2 flex flex-col items-center">
              <h2 className="mb-2 flex items-center justify-center gap-0.5 font-anton text-[11px] font-normal uppercase leading-[12px] tracking-normal [leading-trim:none]">
                <span className="shrink-0 text-white">Friends</span>
                <span className="text-white/40">({friendsStrip.length})</span>
              </h2>
              <div
                className="w-full min-w-0 max-w-full overflow-x-auto overflow-y-hidden overscroll-x-contain pb-1 hide-scrollbar snap-x snap-mandatory touch-pan-x scroll-pl-3 scroll-pr-3"
                aria-label="Friends — swipe or scroll horizontally"
              >
                <div className="mx-auto flex w-max max-w-none flex-nowrap items-center gap-2">
                  {friendsStrip.map((f) => (
                    <ProfileLink
                      key={f.userId}
                      to={
                        f.player_id
                          ? `/profile?p=${encodeURIComponent(f.player_id)}`
                          : `/profile?u=${encodeURIComponent(String(f.userId))}`
                      }
                      state={profileLinkBackground}
                      className="group shrink-0 snap-start"
                    >
                      <ProfileAvatar
                        variant="chip"
                        src={
                          resolveAvatarUrl({
                            profile_pic: f.profile_pic,
                            avatar_url: f.avatar_url,
                          }) ?? ""
                        }
                        displayName={playerHandleName(f)}
                        xpWeekCrown={f.xp_week_crown}
                        avatarFrameUrl={f.avatar_frame_url}
                        avatarFrameSmallUrl={f.avatar_frame_small_url}
                      />
                    </ProfileLink>
                  ))}
                </div>
              </div>
            </section>
          )}

          {!isEditing && (
            <section
              className={`border-t border-white/10 ${
                friendsStrip.length > 0 ? "mt-2 pt-2" : "mt-4 pt-4"
              }`}
            >
              <div className="flex h-[70px] w-full shrink-0 flex-col overflow-hidden rounded-none bg-[#381663] px-3">
                <div className="min-h-0 flex-1" aria-hidden="true" />
                <div className="flex w-full shrink-0 flex-col items-center gap-2">
                  <p className="w-full max-w-[min(100%,280px)] shrink-0 text-center">
                    <span className="font-anton text-[12px] font-normal uppercase leading-[100%] tracking-normal text-[#D2FF00] [leading-trim:none]">
                      Invite friends
                    </span>{" "}
                    <span className="font-anton text-[12px] font-normal uppercase leading-[100%] tracking-normal text-white [leading-trim:none]">
                      with your profile link
                    </span>
                  </p>
                  <div className="mb-2 flex w-full min-w-0 max-w-full items-stretch rounded-none">
                    <div className="box-border flex min-h-[26px] min-w-0 flex-1 items-center gap-2 border border-solid border-[#D2FF00] px-2 py-0.5">
                      <p
                        className="min-w-0 flex-1 truncate text-left font-chakra text-[9px] font-normal leading-[14px] tracking-normal text-white [leading-trim:none]"
                        title={inviteUrl}
                      >
                        {inviteUrlDisplay || "—"}
                      </p>
                      <button
                        type="button"
                        onClick={handleCopyInvite}
                        className="flex h-4 w-4 shrink-0 items-center justify-center opacity-90 transition-opacity hover:opacity-100 disabled:opacity-40"
                        aria-label="Copy link"
                        disabled={!inviteUrl}
                      >
                        <img
                          src="/assets/copy.svg"
                          alt=""
                          className="h-4 w-4 object-contain"
                          width={16}
                          height={16}
                          aria-hidden
                        />
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={
                        canNativeShare ? handleShareInvite : handleCopyInvite
                      }
                      disabled={!inviteUrl}
                      className="flex min-w-[76px] shrink-0 items-center justify-center bg-[#D2FF00] px-4 font-anton text-[11px] font-normal uppercase text-black hover:brightness-95 disabled:opacity-50"
                    >
                      {inviteCopied ? "Copied" : "Share"}
                    </button>
                  </div>
                </div>
                <div className="min-h-0 flex-1" aria-hidden="true" />
              </div>
            </section>
          )}

          {!isEditing && user && (
            <section className="mb-2 mt-6">
              <h2 className="mb-3 text-center font-anton text-[11px] font-normal uppercase leading-[12px] tracking-wide text-white [leading-trim:none]">
                Activity
              </h2>
              <FriendActivityTimeline
                digests={ownActivityDigests}
                liveFriends={ownActivityLiveFriends}
                myTrades={ownActivityMyTrades}
                mySquares={ownActivityMySquares}
                notifications={[]}
                profileModalState={profileLinkBackground}
                loading={ownActivityLoading}
                visibleCount={10}
                surface="profile"
                enableInfiniteScroll
                infiniteScrollStep={10}
                isOwnProfileView
                hasMoreServerRows={ownActivityHasMore}
                serverLoadingMore={ownActivityLoadingMore}
                onReachEnd={() => {
                  if (ownActivityLoadingMore || !ownActivityHasMore) return;
                  setOwnActivityResultsLimit((prev) => prev + 10);
                }}
                onProfileLinkClick={() => onClose?.()}
                emptyMessage="No recent activity yet."
              />
            </section>
          )}
        </div>
      )}

      <AvatarChooserModal
        isOpen={avatarChooserOpen}
        onRequestClose={closeAvatarChooser}
        avatarOptions={stripAvatarOptions}
        initialSelectedKey={avatarChooserInitialKey}
      />
      <XpHubModal
        isOpen={xpHubOpen}
        onClose={() => setXpHubOpen(false)}
        xp={careerSummary?.xp ?? null}
      />
    </div>
  );
}
